import { InspectionService } from '../services/inspections';
import { features } from '../config/platform';

const COOKIE_EXPIRY_MS = 10 * 60 * 1000;
// Keep a 5s buffer so the refresh API can complete and return new cookies before expiry.
const COOKIE_REFRESH_BUFFER_MS = 5 * 1000;
export const COOKIE_REFRESH_INTERVAL_MS = COOKIE_EXPIRY_MS - COOKIE_REFRESH_BUFFER_MS;
export const COOKIE_REFRESH_RETRY_MS = 60 * 1000;

interface Registration {
  timer: ReturnType<typeof setTimeout> | null;
  refCount: number;
}

// Module-level so every caller (the assets hook, the damages query cache,
// anything else that needs an inspection's play cookies kept alive) shares
// one timer per inspectionId instead of each running its own independent
// polling loop against the same refresh endpoint.
const registrations = new Map<string, Registration>();

const scheduleNext = (inspectionId: string, delayMs: number) => {
  const registration = registrations.get(inspectionId);
  if (!registration) {
    return;
  }

  registration.timer = setTimeout(async () => {
    try {
      await InspectionService.refreshInspectionPlayCookies(inspectionId);
      scheduleNext(inspectionId, COOKIE_REFRESH_INTERVAL_MS);
    } catch (error) {
      console.error('Failed to refresh inspection play cookies', error);
      scheduleNext(inspectionId, COOKIE_REFRESH_RETRY_MS);
    }
  }, delayMs);
};

/**
 * Ensures inspection play cookies are periodically refreshed for `inspectionId`
 * while at least one caller holds a registration. Call the returned release
 * function once this caller no longer needs cookies kept alive (unmount,
 * cache eviction, fetch failure, etc.) - the shared timer keeps running as
 * long as another caller still holds a registration for the same id, and
 * stops once the last one releases.
 */
export const acquireInspectionPlayCookieRefresh = (inspectionId: string): (() => void) => {
  // Signed play cookies are a cloud-portal concept; on-prem deployments
  // don't need them refreshed.
  if (!inspectionId || !features.isCloudPortal) {
    return () => {};
  }

  let registration = registrations.get(inspectionId);

  if (!registration) {
    registration = { timer: null, refCount: 0 };
    registrations.set(inspectionId, registration);
    scheduleNext(inspectionId, COOKIE_REFRESH_INTERVAL_MS);
  }

  registration.refCount += 1;

  let released = false;

  return () => {
    if (released) {
      return;
    }
    released = true;

    const current = registrations.get(inspectionId);
    if (!current) {
      return;
    }

    current.refCount -= 1;

    if (current.refCount <= 0) {
      if (current.timer) {
        clearTimeout(current.timer);
      }
      registrations.delete(inspectionId);
    }
  };
};
